/*
 * Single sign-on — Identity sub-surface.
 *
 * Operator management of the external identity providers people sign in through (Microsoft Entra ID,
 * Google Workspace, Okta, or a generic OIDC provider) and the directory-group / role mappings that
 * govern how a federated user is provisioned. Sibling of Connected Applications: that surface is apps
 * that sign in USING Prove7; this is providers Prove7 federates users FROM.
 *
 * Backend: /api/v1/identity/sso (Org Admin for a tenant provider; Platform Admin for a global one).
 * The client secret is write-only — entered here, stored in the secret manager, never returned.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ShieldCheck, Copy } from 'lucide-react';

import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { FieldGroup, Input, Select } from '../components/v2/Input';
import SsoDomainsPanel from '../components/v2/SsoDomainsPanel';
import { useToast } from '../contexts/ToastContext';
import {
  listSsoProviders, getSsoProvider, createSsoProvider, updateSsoProvider,
  enableSsoProvider, disableSsoProvider, deleteSsoProvider,
  listSsoGroupMappings, addSsoGroupMapping, deleteSsoGroupMapping,
  listSsoRoleMappings, addSsoRoleMapping, deleteSsoRoleMapping,
  getGroups,
} from '../services/api';

const PROVIDER_KINDS = [
  { value: 'ENTRA', label: 'Microsoft Entra ID' },
  { value: 'GOOGLE', label: 'Google Workspace' },
  { value: 'OKTA', label: 'Okta' },
  { value: 'GENERIC_OIDC', label: 'Generic OIDC' },
];
// Roles a federated user may be provisioned into. Provisioning caps mapped roles at Org Admin
// server-side, so platform-operator roles are intentionally not offered here.
const ROLE_OPTIONS = ['Viewer', 'Auditor', 'Developer', 'Org Admin'];

const kindLabel = (k) => PROVIDER_KINDS.find((p) => p.value === k)?.label || k;

const emptyDraft = () => ({
  providerKind: 'ENTRA', displayName: '', enabled: true, scope: 'tenant',
  issuer: '', discoveryUrl: '', clientId: '', clientSecret: '', redirectUri: '',
  scopes: 'openid email profile', allowedEmailDomains: '',
  jitProvisioningEnabled: true, defaultRole: 'Viewer',
});

export default function IdentitySsoPageV2() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null); // provider id, or '__new__' for the create form
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupMappings, setGroupMappings] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [newGroupMap, setNewGroupMap] = useState({ idpGroupRef: '', prove7GroupId: '', grantRole: '' });
  const [newRoleMap, setNewRoleMap] = useState({ idpClaimValue: '', prove7Role: 'Viewer' });

  const isNew = selectedId === '__new__';

  const loadProviders = async (selectAfter) => {
    setLoading(true);
    try {
      const res = await listSsoProviders();
      if (!res?.ok) {
        addToast({ tone: 'danger', title: 'Could not load sign-in providers', description: res?.error });
        setProviders([]);
        return;
      }
      const list = Array.isArray(res.data?.providers) ? res.data.providers : [];
      setProviders(list);
      if (selectAfter) {
        selectProvider(selectAfter);
      } else if (!selectedId && list.length) {
        selectProvider(list[0].id);
      } else if (list.length === 0) {
        setSelectedId(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProviders(); getGroups().then((r) => { if (r?.ok) setGroups(r.data?.groups || r.data || []); }); }, []);

  const selectProvider = async (id) => {
    setSelectedId(id);
    const res = await getSsoProvider(id);
    if (!res?.ok) {
      addToast({ tone: 'danger', title: 'Could not open provider', description: res?.error });
      return;
    }
    const p = res.data;
    setDraft({
      providerKind: p.providerKind, displayName: p.displayName || '', enabled: !!p.enabled,
      scope: p.scope || 'tenant', issuer: p.issuer || '', discoveryUrl: p.discoveryUrl || '',
      clientId: p.clientId || '', clientSecret: '', redirectUri: p.redirectUri || '',
      scopes: p.scopes || 'openid email profile', allowedEmailDomains: p.allowedEmailDomains || '',
      jitProvisioningEnabled: !!p.jitProvisioningEnabled, defaultRole: p.defaultRole || 'Viewer',
      hasClientSecret: !!p.hasClientSecret,
    });
    loadMappings(id);
  };

  const loadMappings = async (id) => {
    const [g, r] = await Promise.all([listSsoGroupMappings(id), listSsoRoleMappings(id)]);
    setGroupMappings(g?.ok ? (g.data?.mappings || []) : []);
    setRoleMappings(r?.ok ? (r.data?.mappings || []) : []);
  };

  const openCreate = () => {
    setSelectedId('__new__');
    setDraft(emptyDraft());
    setGroupMappings([]);
    setRoleMappings([]);
  };

  // Clone the open provider into a real, persisted DISABLED copy — so it shows up immediately in the
  // list on the left, not just as unsaved form state. The original is left untouched. Only one
  // provider of a kind can be ACTIVE in a workspace, so the copy is saved disabled; this is the
  // staging half of a blue/green swap: clone → edit the copy → disable the original → enable the copy.
  // The client secret can't be read back, so it is NOT carried over — the copy is saved without one
  // and must have it entered before it can be enabled.
  const cloneProvider = async () => {
    const src = draft;
    const payload = {
      providerKind: src.providerKind,
      displayName: (src.displayName || kindLabel(src.providerKind)) + ' (copy)',
      enabled: false,   // disabled duplicate — always allowed to save
      issuer: (src.issuer || '').trim() || null,
      discoveryUrl: (src.discoveryUrl || '').trim() || null,
      clientId: (src.clientId || '').trim(),
      redirectUri: (src.redirectUri || '').trim(),
      scopes: (src.scopes || '').trim() || 'openid email profile',
      allowedEmailDomains: (src.allowedEmailDomains || '').trim() || null,
      jitProvisioningEnabled: src.jitProvisioningEnabled,
      defaultRole: src.defaultRole,
      scope: src.scope || 'tenant',   // same scope as the source
    };
    setSaving(true);
    try {
      const res = await createSsoProvider(payload);
      if (!res?.ok) {
        addToast({ tone: 'danger', title: 'Could not clone provider', description: res?.error });
        return;
      }
      addToast({
        tone: 'success',
        title: 'Copy saved (disabled)',
        description: 'It is in the list on the left. Open it to add the client secret, then enable it once you disable the original.',
      });
      await loadProviders(res.data?.id);   // refresh list and open the new disabled copy
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const save = async () => {
    if (!draft.displayName.trim()) {
      addToast({ tone: 'danger', title: 'Name required', description: 'Give the provider a display name.' });
      return;
    }
    if (!draft.clientId.trim()) {
      addToast({ tone: 'danger', title: 'Client ID required', description: 'Enter the application (client) ID from your provider.' });
      return;
    }
    if (!draft.redirectUri.trim()) {
      addToast({ tone: 'danger', title: 'Redirect URL required', description: 'Enter the callback URL registered with your provider.' });
      return;
    }
    const payload = {
      providerKind: draft.providerKind,
      displayName: draft.displayName.trim(),
      enabled: draft.enabled,
      issuer: draft.issuer.trim() || null,
      discoveryUrl: draft.discoveryUrl.trim() || null,
      clientId: draft.clientId.trim(),
      redirectUri: draft.redirectUri.trim(),
      scopes: draft.scopes.trim() || 'openid email profile',
      allowedEmailDomains: draft.allowedEmailDomains.trim() || null,
      jitProvisioningEnabled: draft.jitProvisioningEnabled,
      defaultRole: draft.defaultRole,
    };
    // Client secret is only sent when the operator entered one (blank keeps the stored value).
    if (draft.clientSecret.trim()) payload.clientSecret = draft.clientSecret.trim();
    // Scope is chosen at creation; the server resolves 'tenant' to the caller's workspace and gates
    // 'global' to Platform Admin. Immutable afterwards, so only sent on create.
    if (isNew) payload.scope = draft.scope;
    setSaving(true);
    try {
      const res = isNew ? await createSsoProvider(payload) : await updateSsoProvider(selectedId, payload);
      if (!res?.ok) {
        addToast({ tone: 'danger', title: isNew ? 'Could not create provider' : 'Could not save changes', description: res?.error });
        return;
      }
      addToast({ tone: 'success', title: isNew ? 'Provider created' : 'Changes saved' });
      await loadProviders(res.data?.id);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (isNew) { set('enabled', !draft.enabled); return; }
    const res = draft.enabled ? await disableSsoProvider(selectedId) : await enableSsoProvider(selectedId);
    if (!res?.ok) {
      addToast({ tone: 'danger', title: 'Could not update provider', description: res?.error });
      return;
    }
    set('enabled', !draft.enabled);
    await loadProviders(selectedId);
  };

  const removeProvider = async () => {
    if (isNew) { setSelectedId(providers[0]?.id || null); return; }
    const res = await deleteSsoProvider(selectedId);
    if (!res?.ok) {
      addToast({ tone: 'danger', title: 'Could not remove provider', description: res?.error });
      return;
    }
    addToast({ tone: 'success', title: 'Provider removed' });
    setSelectedId(null);
    await loadProviders();
  };

  const addGroupMap = async () => {
    if (!newGroupMap.idpGroupRef.trim() || !newGroupMap.prove7GroupId) {
      addToast({ tone: 'danger', title: 'Group mapping incomplete', description: 'Enter the directory group and choose a Prove7 group.' });
      return;
    }
    const res = await addSsoGroupMapping(selectedId, {
      idpGroupRef: newGroupMap.idpGroupRef.trim(),
      prove7GroupId: newGroupMap.prove7GroupId,
      grantRole: newGroupMap.grantRole || null,
    });
    if (!res?.ok) { addToast({ tone: 'danger', title: 'Could not add mapping', description: res?.error }); return; }
    setNewGroupMap({ idpGroupRef: '', prove7GroupId: '', grantRole: '' });
    loadMappings(selectedId);
  };

  const addRoleMap = async () => {
    if (!newRoleMap.idpClaimValue.trim()) {
      addToast({ tone: 'danger', title: 'Role mapping incomplete', description: 'Enter the role claim value.' });
      return;
    }
    const res = await addSsoRoleMapping(selectedId, {
      idpClaimValue: newRoleMap.idpClaimValue.trim(), prove7Role: newRoleMap.prove7Role,
    });
    if (!res?.ok) { addToast({ tone: 'danger', title: 'Could not add mapping', description: res?.error }); return; }
    setNewRoleMap({ idpClaimValue: '', prove7Role: 'Viewer' });
    loadMappings(selectedId);
  };

  const groupName = (id) => groups.find((g) => g.id === id)?.name || id;

  return (
    <V2PageShell
      title="Single sign-on"
      subtitle="External identity providers your people sign in through."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="ghost" iconLeft={<ArrowLeft size={16} />} onClick={() => navigate('/identity')}>
            Back to Identity
          </Button>
          <Button variant="primary" iconLeft={<Plus size={16} />} onClick={openCreate}>
            Add provider
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Providers list */}
        <Card padding="none">
          {loading ? (
            <p className="text-[13px] text-v2-ink-secondary p-3 m-0">Loading providers…</p>
          ) : providers.length === 0 && !isNew ? (
            <div className="p-3">
              <p className="text-[13px] text-v2-ink-secondary m-0">No providers yet.</p>
            </div>
          ) : (
            <div className="flex flex-col p-2 gap-1">
              {providers.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectProvider(p.id)}
                  aria-current={selectedId === p.id ? 'true' : undefined}
                  className={
                    'relative text-left rounded-lg border p-2.5 pl-3 transition-colors ' +
                    (selectedId === p.id
                      ? 'border-v2-action bg-v2-action-soft ring-1 ring-inset ring-v2-action'
                      : 'border-v2-border hover:border-v2-ink-secondary')
                  }
                >
                  {selectedId === p.id && (
                    <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-v2-action" />
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[13px] font-medium text-v2-ink">{p.displayName || kindLabel(p.providerKind)}</div>
                    {selectedId === p.id && (
                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-v2-action-strong">Editing</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    <Pill tone={p.enabled ? 'success' : 'neutral'}>{p.enabled ? 'Enabled' : 'Disabled'}</Pill>
                    <span className="text-[11px] text-v2-ink-secondary">{p.scope === 'global' ? 'Global' : 'This workspace'}</span>
                  </div>
                </button>
              ))}
              {isNew && (
                <div className="relative rounded-lg border border-v2-action bg-v2-action-soft p-2.5 pl-3 ring-1 ring-inset ring-v2-action">
                  <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg bg-v2-action" />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-medium text-v2-ink">New provider…</span>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-v2-action-strong">Editing</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Editor */}
        {selectedId ? (
          <Card>
            <div className="flex flex-col gap-4 p-1">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-v2-ink m-0">
                  {isNew ? 'Add identity provider' : (draft.displayName || kindLabel(draft.providerKind))}
                </h3>
                <label className="inline-flex items-center gap-2 text-[13px] text-v2-ink-secondary cursor-pointer">
                  <input type="checkbox" checked={draft.enabled} onChange={toggleEnabled} />
                  Enabled
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FieldGroup label="Provider type">
                  {isNew ? (
                    <Select value={draft.providerKind} onChange={(e) => set('providerKind', e.target.value)} ariaLabel="Provider type">
                      {PROVIDER_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </Select>
                  ) : (
                    <Input value={kindLabel(draft.providerKind)} disabled />
                  )}
                </FieldGroup>
                <FieldGroup label="Display name" required>
                  <Input value={draft.displayName} onChange={(e) => set('displayName', e.target.value)} placeholder="e.g. Contoso Entra ID" />
                </FieldGroup>
                {isNew && (
                  <FieldGroup label="Availability">
                    <Select value={draft.scope} onChange={(e) => set('scope', e.target.value)} ariaLabel="Availability">
                      <option value="tenant">This workspace</option>
                      <option value="global">All workspaces (global)</option>
                    </Select>
                  </FieldGroup>
                )}
                <FieldGroup label="Issuer" hint="Your provider's OIDC issuer URL.">
                  <Input value={draft.issuer} onChange={(e) => set('issuer', e.target.value)} placeholder="https://login.microsoftonline.com/…/v2.0" className="font-mono" />
                </FieldGroup>
                <FieldGroup label="Discovery URL" hint="Optional. Defaults to the issuer's .well-known document.">
                  <Input value={draft.discoveryUrl} onChange={(e) => set('discoveryUrl', e.target.value)} placeholder="Leave blank to derive from issuer" className="font-mono" />
                </FieldGroup>
                <FieldGroup label="Client ID" required>
                  <Input value={draft.clientId} onChange={(e) => set('clientId', e.target.value)} className="font-mono" />
                </FieldGroup>
                <FieldGroup label="Client secret" hint={draft.hasClientSecret ? 'A secret is stored. Enter a new value to replace it.' : 'Stored securely and never shown again.'}>
                  <Input type="password" value={draft.clientSecret} onChange={(e) => set('clientSecret', e.target.value)}
                    placeholder={draft.hasClientSecret ? 'Leave blank to keep current secret' : 'Paste the client secret'} />
                </FieldGroup>
                <FieldGroup label="Redirect URL" required hint="The callback URL registered with your provider.">
                  <Input value={draft.redirectUri} onChange={(e) => set('redirectUri', e.target.value)} className="font-mono"
                    placeholder="https://your-host/auth/sso/callback" />
                </FieldGroup>
                <FieldGroup label="Scopes">
                  <Input value={draft.scopes} onChange={(e) => set('scopes', e.target.value)} className="font-mono" />
                </FieldGroup>
                <FieldGroup label="Allowed email domains" hint="Comma-separated. Leave blank to allow any domain.">
                  <Input value={draft.allowedEmailDomains} onChange={(e) => set('allowedEmailDomains', e.target.value)}
                    placeholder="prove7.com, acme.com" />
                </FieldGroup>
                <FieldGroup label="Default role for new users">
                  <Select value={draft.defaultRole} onChange={(e) => set('defaultRole', e.target.value)} ariaLabel="Default role">
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </Select>
                </FieldGroup>
                <FieldGroup label="Just-in-time provisioning" hint="Create a Prove7 account on first sign-in.">
                  <label className="inline-flex items-center gap-2 text-[13px] text-v2-ink cursor-pointer h-9">
                    <input type="checkbox" checked={draft.jitProvisioningEnabled}
                      onChange={(e) => set('jitProvisioningEnabled', e.target.checked)} />
                    {draft.jitProvisioningEnabled ? 'On — new users become ' + draft.defaultRole : 'Off — only existing users can sign in'}
                  </label>
                </FieldGroup>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="primary" onClick={save} loading={saving}>{isNew ? 'Create provider' : 'Save changes'}</Button>
                {!isNew && <Button variant="secondary" onClick={cloneProvider} iconLeft={<Copy size={15} />}>Clone</Button>}
                {!isNew && <Button variant="ghost" onClick={removeProvider} iconLeft={<Trash2 size={15} />}>Remove</Button>}
              </div>

              {!isNew && selectedId && (
                <div className="mt-5 pt-4 border-t border-v2-border">
                  <h4 className="text-[13px] font-semibold text-v2-ink m-0 mb-2">Verified sign-in domains</h4>
                  <SsoDomainsPanel providerId={selectedId} />
                </div>
              )}

              {/* Mappings — only once the provider exists */}
              {!isNew && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-v2-border pt-4">
                  <div>
                    <h4 className="text-[13px] font-semibold text-v2-ink m-0 mb-2">Group mappings</h4>
                    <p className="text-[12px] text-v2-ink-secondary mt-0 mb-2">Mapped groups sync at each sign-in; manual memberships are left untouched.</p>
                    <div className="flex flex-col gap-1 mb-2">
                      {groupMappings.map((m) => (
                        <div key={m.id} className="flex items-center justify-between text-[12px] border border-v2-border rounded-lg px-2.5 py-1.5">
                          <span><code className="text-v2-ink-secondary">{m.idpGroupRef}</code> → {groupName(m.prove7GroupId)}{m.grantRole ? ` · ${m.grantRole}` : ''}</span>
                          <button type="button" aria-label="Remove mapping" className="text-v2-ink-secondary hover:text-v2-danger"
                            onClick={() => deleteSsoGroupMapping(selectedId, m.id).then(() => loadMappings(selectedId))}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      {groupMappings.length === 0 && <span className="text-[12px] text-v2-ink-secondary">No group mappings yet.</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Input value={newGroupMap.idpGroupRef} onChange={(e) => setNewGroupMap((v) => ({ ...v, idpGroupRef: e.target.value }))}
                        placeholder="Directory group (id or name)" />
                      <Select value={newGroupMap.prove7GroupId} onChange={(e) => setNewGroupMap((v) => ({ ...v, prove7GroupId: e.target.value }))} ariaLabel="Prove7 group">
                        <option value="">Choose a Prove7 group…</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </Select>
                      <Select value={newGroupMap.grantRole} onChange={(e) => setNewGroupMap((v) => ({ ...v, grantRole: e.target.value }))} ariaLabel="Grant role">
                        <option value="">Group access only (no tenant role)</option>
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{`Also grant ${r} in the group's tenant`}</option>)}
                      </Select>
                      <Button variant="secondary" iconLeft={<Plus size={14} />} onClick={addGroupMap}>Add group mapping</Button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-[13px] font-semibold text-v2-ink m-0 mb-2">Role mappings</h4>
                    <p className="text-[12px] text-v2-ink-secondary mt-0 mb-2">Highest matching role wins, capped at Org Admin.</p>
                    <div className="flex flex-col gap-1 mb-2">
                      {roleMappings.map((m) => (
                        <div key={m.id} className="flex items-center justify-between text-[12px] border border-v2-border rounded-lg px-2.5 py-1.5">
                          <span><code className="text-v2-ink-secondary">{m.idpClaimValue}</code> → {m.prove7Role}</span>
                          <button type="button" aria-label="Remove mapping" className="text-v2-ink-secondary hover:text-v2-danger"
                            onClick={() => deleteSsoRoleMapping(selectedId, m.id).then(() => loadMappings(selectedId))}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      {roleMappings.length === 0 && <span className="text-[12px] text-v2-ink-secondary">No role mappings yet.</span>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Input value={newRoleMap.idpClaimValue} onChange={(e) => setNewRoleMap((v) => ({ ...v, idpClaimValue: e.target.value }))}
                        placeholder="Role claim value (e.g. prove7.developer)" />
                      <Select value={newRoleMap.prove7Role} onChange={(e) => setNewRoleMap((v) => ({ ...v, prove7Role: e.target.value }))} ariaLabel="Prove7 role">
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </Select>
                      <Button variant="secondary" iconLeft={<Plus size={14} />} onClick={addRoleMap}>Add role mapping</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <EmptyState
              icon={<ShieldCheck size={20} />}
              title="No sign-in providers yet"
              description="Add an identity provider so your people can sign in with their existing directory account."
              primaryAction={<Button variant="primary" iconLeft={<Plus size={16} />} onClick={openCreate}>Add provider</Button>}
            />
          </Card>
        )}
      </div>
    </V2PageShell>
  );
}
